import classes from "./NewPackagingSelectionItem.module.less";
import type { PackagingInformationEntry } from "~/models/ProductData";
import { Card } from "~/Configurators/Linear/components/Card";
import { type ReactNode, useMemo, useState } from "react";
import { ToggleIcon } from "~/common/components/icon/ToggleIcon";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { IconButton, Stack, Typography, type TypographyProps } from "@mui/material";
import { Collapse } from "../../Collapse";
import { getStockLevel, StockIndicatorIcon } from "~/common/components/StockIndicator/StockIndicator";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Divider } from "~/common/components/Divider";
import {
  AliasesParameter,
  CountryOfOriginDescriptorParam,
  EANParameter,
} from "~/common/components/Product/ProductParams";
import { useTranslation } from "react-i18next";
import { getPackagingLabel } from "~/feature/cart/cart/utils/cartUtils";
import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import dayjs from "dayjs";
import { DateFormatType, formatDate } from "~/utils/DateFormatter";
import { useIsPackagePhasedOut } from "~/ProductDetails/components/PhaseInOut/usePhaseInOutLogic";
import { DiscontinuationNotification } from "~/ProductDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { PackagingSelectionVariant } from "~/common/components/PackagingSelection/PackagingSelectionVariant";
import { Stacker } from "~/common/components/Stacker/Stacker";
import classNames from "classnames";
import Parameter from "~/common/components/Parameter";
import { calculateSalesCampaign4CampaignPage } from "~/SalesCampaign/utils";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { useSalesData } from "~/SalesCampaign/useSalesData";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { NumberFormatter } from "~/utils/NumberFormatter";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { showDateAsCalendarWeek } from "~/utils/features";

interface Props {
  className?: string;
  pack: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
  selected?: boolean;
  onSelect?: () => void;
  initiallyExpanded?: boolean;
  variant: PackagingSelectionVariant;
  disabled?: boolean;
}

const RESULT_NF = NumberFormatter.getFormatter();

export const NewPackagingSelectionItem = ({
  selected,
  initiallyExpanded,
  onSelect,
  productDescriptor,
  pack,
  variant,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.tablet;

  const [{ stockExpanded, productionExpanded, miscExpanded }, setState] = useState(() => ({
    stockExpanded: initiallyExpanded,
    productionExpanded: initiallyExpanded,
    miscExpanded: initiallyExpanded,
  }));
  const allExpanded = stockExpanded && productionExpanded && miscExpanded;
  const toggleAll = () => {
    setState({
      stockExpanded: !allExpanded,
      productionExpanded: !allExpanded,
      miscExpanded: !allExpanded,
    });
  };
  const makeToggle = (key: "stockExpanded" | "productionExpanded" | "miscExpanded") => () => {
    setState((state) => ({ ...state, [key]: !state[key] }));
  };

  const saleForPack = useSalesData()?.salePerPack?.[pack.packCode];

  const packagingLabel = getPackagingLabel({
    t,
    productDescriptor,
    packSize: pack.packSize,
    packCode: pack.packCode,
  });

  const prefQuantity = pack.preferredPackQuantity;

  const showDiscontinuedWarning = useIsPackagePhasedOut(productDescriptor, pack.packCode);

  const showSectionToggles =
    variant === PackagingSelectionVariant.PDP || (variant === PackagingSelectionVariant.SRP && isMobile);
  const showPreferredQuantity =
    variant === PackagingSelectionVariant.PDP || (variant === PackagingSelectionVariant.SRP && isMobile);
  const showMiscSection =
    variant === PackagingSelectionVariant.PDP || (variant === PackagingSelectionVariant.SRP && isMobile);

  const separateHeader =
    (!isMobile && variant === PackagingSelectionVariant.SRP) || variant === PackagingSelectionVariant.Campaign;

  const showMiscParamsInHeader = !isMobile && variant === PackagingSelectionVariant.SRP;
  const showParamsInHeader = showMiscParamsInHeader;

  const stockCalculations = useInStockCalculations(pack);

  const showExpandAll = variant !== PackagingSelectionVariant.Campaign;
  const showMainContent = variant !== PackagingSelectionVariant.Campaign;

  const mainContent = (
    <>
      {showPreferredQuantity && !!prefQuantity && (
        <p className={classes.preferredPackaging}> {t("addToCartPopup.prefPackQuantity", { count: prefQuantity })}</p>
      )}
      <InStockSection
        expanded={separateHeader || stockExpanded}
        onToggle={makeToggle("stockExpanded")}
        pack={pack}
        showToggle={showSectionToggles}
        {...stockCalculations}
      />
      <InProductionSection
        expanded={separateHeader || productionExpanded}
        onToggle={makeToggle("productionExpanded")}
        pack={pack}
        showToggle={showSectionToggles}
      />
      {showMiscSection && (
        <MiscSection
          expanded={separateHeader || miscExpanded}
          onToggle={makeToggle("miscExpanded")}
          pack={pack}
          productDescriptor={productDescriptor}
          showToggle={showSectionToggles}
        />
      )}
    </>
  );

  const discontinuationNotification = showDiscontinuedWarning && (
    <DiscontinuationNotification
      className={classes.discontinuationNotification}
      discontinued={productDescriptor}
      variant={"package"}
    />
  );

  const header = (
    <Header
      expanded={allExpanded}
      onToggle={toggleAll}
      showToggle={showExpandAll}
      className={separateHeader ? classes.separateHeader : undefined}
    >
      <Stacker direction={"row"} gap={8} className={classes.perPack}>
        <IconButton className={classes.checkedIndicator} onClick={onSelect} disabled={disabled}>
          <CheckedCircle checked={selected} alwaysVisible />
        </IconButton>
        <p className={classes.title}>{packagingLabel}</p>
      </Stacker>
      {saleForPack && (
        <SaleTag className={classes.saleTag} size={20} variant={saleForPack.soldOut ? "inactive" : "active"} />
      )}
      {showParamsInHeader && (
        <>
          <Divider variant={"fullWidth"} orientation={"vertical"} flexItem className={classes.divider} />
          {showMiscParamsInHeader && (
            <div className={classes.separateHeaderParams}>
              {stockCalculations.totalInStock > 0 && (
                <Parameter label={"Availability"}>
                  <StockIndicatorValue totalInStock={stockCalculations.totalInStock} />
                </Parameter>
              )}
              {stockCalculations.totalInStock === 0 && (
                <Parameter label={<StockIndicatorValue totalInStock={0} />}>
                  <ReplenishmentDate date={pack.replenishmentLeadTimeDate} component={"span"} />
                </Parameter>
              )}
              <EANParameter showEmpty product={productDescriptor.base} packCode={pack.packCode} />
              <CountryOfOriginDescriptorParam showEmpty descriptor={productDescriptor} packCode={pack.packCode} />
              <AliasesParameter
                showEmpty
                product={productDescriptor.base}
                mode={"15-digits"}
                maxAliasesVisible={1}
                packCode={pack.packCode}
              />
            </div>
          )}
        </>
      )}
    </Header>
  );

  let content = (
    <CardContent>
      {discontinuationNotification}
      {!separateHeader && header}
      {showMainContent && mainContent}
    </CardContent>
  );

  if (separateHeader) {
    content = (
      <Collapse in={allExpanded} classes={{ wrapperInner: classes.collapsibleCardContent }}>
        {content}
      </Collapse>
    );
  }

  return (
    <Card className={classes.root} active={selected}>
      {separateHeader && <CardContent className={classes.separateHeaderContainer}>{header}</CardContent>}
      {content}
    </Card>
  );
};

interface CollapsibleSectionProps {
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
  canExpand?: boolean;
  title: ReactNode;
  children: ReactNode;
}

const CollapsibleSection = ({
  expanded,
  onToggle,
  showToggle,
  canExpand,
  children,
  title,
}: CollapsibleSectionProps) => {
  return (
    <Card className={classes.collapsibleCard}>
      <Header expanded={expanded} onToggle={onToggle} showToggle={showToggle}>
        {title}
      </Header>
      {canExpand && (
        <Collapse in={expanded} classes={{ wrapperInner: classes.collapsibleCardContent }}>
          {children}
        </Collapse>
      )}
    </Card>
  );
};

interface HeaderProps {
  children: ReactNode;
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
  className?: string;
}

const Header = ({ children, expanded, onToggle, showToggle, className }: HeaderProps) => {
  return (
    <div className={classNames(classes.header, className)}>
      {children}
      {showToggle && (
        <IconButton className={classes.toggle} onClick={onToggle} color={"primary"}>
          <ToggleIcon isActive={expanded} />
        </IconButton>
      )}
    </div>
  );
};

const CollapsedTitle = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <Divider orientation={"vertical"} flexItem />
      <p className={classes.collapsedTitle}>{children}</p>
    </>
  );
};

type StockTableProps = {
  warehouseName: string;
  stock: (readonly [number, string])[];
  salesCampaignStock?: number;
};

const StockTable = ({ warehouseName, stock, salesCampaignStock = 0 }: StockTableProps) => {
  const { t } = useTranslation();
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t("productDetailsPage.availabilityTable.units")}</TableCell>
          <TableCell>
            {window.SCHA.config.showESD
              ? warehouseName
              : t("productDetailsPage.availabilityTable.estimatedArrivalDate")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {salesCampaignStock > 0 && (
          <TableRow>
            <TableCell>{NumberFormatter.getDefaultFormatter().format(salesCampaignStock)}</TableCell>
            <TableCell>
              <span>{t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" })}</span>
              <span className={classes.salesTableTag}>
                {t("feature.salesCampaign.specialDealPrice")}
                <SaleTag className={"scha__pkg-selection__table__sales-tag"} size={20} />
              </span>
            </TableCell>
          </TableRow>
        )}
        {stock.map(([amount, date], index) => (
          <TableRow key={index}>
            <TableCell>{NumberFormatter.getDefaultFormatter().format(amount)}</TableCell>
            <TableCell>{date}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface SectionProps {
  pack: PackagingInformationEntry;
  expanded?: boolean;
  onToggle: () => void;
  showToggle?: boolean;
}

const InStockSection = ({
  pack,
  expanded,
  onToggle,
  showToggle = true,
  readyForShipment,
  totalInStock,
  inStockEntries,
}: SectionProps & ReturnType<typeof useInStockCalculations>) => {
  const { t } = useTranslation();

  const warehouseName = translateWarehouseName(t, pack.primaryWarehouseName);
  const salesStock = useSalesData()?.salePerPack?.[pack.packCode]?.salesStock;
  const firstStockEntry = inStockEntries[0];

  const canExpand = totalInStock > 0;
  const quantity = firstStockEntry ? Number(firstStockEntry[0]) : 0;

  return (
    <CollapsibleSection
      expanded={expanded && canExpand}
      onToggle={onToggle}
      showToggle={showToggle && canExpand}
      canExpand={canExpand}
      title={
        <>
          <StockIndicatorValue className={classes.sectionTitle} totalInStock={totalInStock} />
          {!expanded && firstStockEntry && canExpand && (
            <CollapsedTitle>
              {t("productDetailsPage.availabilityTable.upcoming", {
                quantity: isNaN(quantity) ? firstStockEntry[0] : RESULT_NF.format(quantity),
                date: firstStockEntry[1],
              })}
            </CollapsedTitle>
          )}
        </>
      }
    >
      <StockTable warehouseName={warehouseName} stock={inStockEntries} salesCampaignStock={salesStock} />
    </CollapsibleSection>
  );
};

const useInStockCalculations = (pack: PackagingInformationEntry) => {
  const { t } = useTranslation();
  return useMemo(() => {
    let inStockEntries;
    let readyForShipment;
    if (showDateAsCalendarWeek()) {
      inStockEntries = pack.totalWarehouse.inStockWeekly.map((entry) => {
        const formattedDate = entry.cwDate
          ? entry.cwDate
          : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" });
        return [entry.quantity, formattedDate] as const;
      });
      readyForShipment = pack.totalWarehouse.inStockWeekly.find((entry) => !entry.cwDate)?.quantity ?? 0;
    } else {
      inStockEntries = pack.totalWarehouse.inStock.map((entry) => {
        const formattedDate = entry.date
          ? formatDate(entry.date, DateFormatType.DATE)
          : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" });
        return [entry.quantity, formattedDate] as const;
      });
      readyForShipment = pack.totalWarehouse.inStock.find((entry) => !entry.date)?.quantity ?? 0;
    }

    const totalInStock = inStockEntries.reduce((acc, [quantity]) => acc + quantity, 0);
    return { readyForShipment, inStockEntries, totalInStock };
  }, [pack, t]);
};

const StockIndicatorValue = ({ totalInStock, className }: { totalInStock: number; className?: string }) => {
  const { t } = useTranslation();

  return (
    <Stacker direction={"row"} gap={8} className={classNames("d-flex flex-align-center", className)}>
      <StockIndicatorIcon className={classes.stockIcon} level={getStockLevel(totalInStock)} />
      <p>
        {totalInStock > 0 &&
          t("stockIndicator.existsWithQty", { amount: NumberFormatter.getDefaultFormatter().format(totalInStock) })}
        {totalInStock === 0 && t("stockIndicator.currentlyNotInStock")}
      </p>
    </Stacker>
  );
};

const InProductionSection = ({ pack, expanded, onToggle, showToggle = true }: SectionProps) => {
  const { t } = useTranslation();

  let inProductionEntries;
  if (showDateAsCalendarWeek()) {
    inProductionEntries = pack.totalWarehouse.inProductionWeekly.map((entry) => {
      const formattedDate = entry.cwDate;
      return [entry.quantity, formattedDate] as const;
    });
  } else {
    inProductionEntries = pack.totalWarehouse.inProduction.map((entry) => {
      const formattedDate = dayjs(entry.date).format("DD.MM.YYYY");
      return [entry.quantity, formattedDate] as const;
    });
  }

  const firstUpcomingStock = inProductionEntries[0];
  const stockValue = firstUpcomingStock ? Number(firstUpcomingStock[0]) : 0;

  if (pack.totalWarehouse.inClarification > 0) {
    inProductionEntries.unshift([
      pack.totalWarehouse.inClarification,
      t("product.availability.stock.table.inClarification"),
    ] as const);
  }
  const totalInProduction = inProductionEntries.reduce((acc, [quantity]) => acc + quantity, 0);

  const warehouseName = translateWarehouseName(t, pack.primaryWarehouseName);

  const canExpand = totalInProduction > 0;
  return (
    <CollapsibleSection
      expanded={expanded}
      onToggle={onToggle}
      showToggle={showToggle && canExpand}
      canExpand={canExpand}
      title={
        <>
          <Stack direction={"row"} gap={1} className={classes.perPack}>
            <Icon type={IconType.Calendar} />
            {totalInProduction > 0 && (
              <p className={classes.sectionTitle}>
                {t("productDetailsPage.availabilityTable.toCome", {
                  quantity: RESULT_NF.format(totalInProduction),
                })}
              </p>
            )}
            {totalInProduction === 0 && <ReplenishmentDate date={pack.replenishmentLeadTimeDate} />}
          </Stack>
          {!expanded && firstUpcomingStock && (
            <CollapsedTitle>
              {t("productDetailsPage.availabilityTable.upcoming", {
                quantity: isNaN(stockValue) ? firstUpcomingStock[0] : RESULT_NF.format(stockValue),
                date: firstUpcomingStock[1],
              })}
            </CollapsedTitle>
          )}
        </>
      }
    >
      <StockTable warehouseName={warehouseName} stock={inProductionEntries} />
      <ReplenishmentDate date={pack.replenishmentLeadTimeDate} sx={{ mt: 2 }} />
    </CollapsibleSection>
  );
};

const MiscSection = ({
  pack,
  expanded,
  onToggle,
  productDescriptor,
  showToggle = true,
}: SectionProps & { productDescriptor: ProductDescriptor }) => {
  const { t } = useTranslation();
  return (
    <CollapsibleSection
      expanded={expanded}
      onToggle={onToggle}
      showToggle={showToggle}
      canExpand={true}
      title={
        <>
          <Stacker direction={"row"} gap={8} className={classes.perPack}>
            <Icon type={IconType.InfoCircleOutline} />

            <p className={classes.sectionTitle}>{t("page.plp.tab.moreInfo")}</p>
          </Stacker>
          {!expanded && <CollapsedTitle>{t("productDetailsPage.availabilityTable.miscParams")}</CollapsedTitle>}
        </>
      }
    >
      <div className={classes.miscSection}>
        <div>
          <AliasesParameter
            showEmpty
            product={productDescriptor.base}
            mode={"15-digits"}
            maxAliasesVisible={2}
            packCode={pack.packCode}
          />
        </div>
        <div>
          <EANParameter showEmpty product={productDescriptor.base} packCode={pack.packCode} />
        </div>
        <div>
          <CountryOfOriginDescriptorParam showEmpty descriptor={productDescriptor} packCode={pack.packCode} />
        </div>
      </div>
    </CollapsibleSection>
  );
};

const CardContent = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={classNames(className, classes.cardContent)}>{children}</div>;
};

const SalesCampaignStock = ({ pack, productDescriptor }: Pick<Props, "pack" | "productDescriptor">) => {
  const firstSalesCampaignObj = Object.values(productDescriptor.base.salesCampaign ?? {})?.[0]?.[0];
  const salesCampaignCode = firstSalesCampaignObj?.code;

  const { status: salesStatus, data } = useMemo(
    () => calculateSalesCampaign4CampaignPage({ productDescriptor, salesCampaignCode }),
    [productDescriptor, salesCampaignCode],
  );

  if (salesStatus === "loading") {
    return <CircleSpinner />;
  }

  const salesData = data?.salePerPack?.[pack.packCode];

  if (!salesData) {
    return null;
  }

  return <SaleQuantityIndicator className={classes.salesIndicator} quantity={salesData.salesStock} />;
};

type ReplenishmentDateProps = Omit<TypographyProps, "children"> & {
  date: Date | null;
};

const ReplenishmentDate = ({ date, ...props }: ReplenishmentDateProps) => {
  const { t } = useTranslation();

  let dateString;
  if (date) {
    if (showDateAsCalendarWeek()) {
      const months = Math.ceil(dayjs(date).diff(dayjs(), "month", true));
      dateString = t("common.month", { count: months });
    } else {
      dateString = formatDate(date, DateFormatType.DATE);
    }
  } else {
    dateString = "-";
  }

  return (
    <Typography fontWeight={"normal"} {...props}>
      {t("addProduct.replenishment", { date: "" })}
      <b>{dateString}</b>
    </Typography>
  );
};
